import { FC, memo, MouseEvent, useState } from 'react'
import { FlexboxGrid, Panel, Button } from 'rsuite'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'
import { useFeatureIsOn } from '@growthbook/growthbook-react'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import Image from 'next/image'

import useUrls from '../../../services/useUrls'
import usePrice from '../../../services/usePrice'
import SafeTranslate from '../../common/SafeTranslate'
import { deliveryHelper } from '../../../utils/util'
import { formatPriceNumber } from '../../../utils/math'
import { Product } from '../../../utils/types/Product'
import { getLargePreview, imageLoader } from '../../../utils/images'
import {
  getPricePerPieceUnit,
  getProductSalesCount,
  getSalesUnit,
} from '../../../lib/salesUnit'
import { getPathName } from '../../../lib/product'
import { getTextContentByType, getProductName } from '../../../lib/text'
import { getSupplierDeliveryDetails, isFreeDelivery } from '../../../lib/service'
import { getCountryAndLocaleStrings } from '../../../utils/locales'
import SaveProductButton from '../../MyProducts/SaveProductButton'
import NameTagContract from '../../CustomPricing/NameTagContract'
import { useAuth } from '../../../services/useAuth'
import useGetAnalyticsLocation from '../../../services/analytics/useGetAnalyticsLocation'
import ProductEmptyImage from '../../SingleProduct/ProductEmptyImage'
import { getVariantType } from '../../../lib/variant'
import CartIcon from '../../Icons/CartIcon'
import useTrackingContext from '../../../services/trackingContext/useTrackingContext'
import { calculateStrikeThroughPrice } from '../../../lib/resources/price'
import { ReviewStarsWithDisplayedAverage } from '../../SingleProduct/ReviewsSection/ReviewStars'
import useReviewFetch from '../../../services/useReviewFetch'
import { getAverageOfReviews } from '../../../utils/reviewUtils'
import useRFQModal from '../../Modals/hooks/useRFQModal'
import useIsOnMobile from '../../../services/useIsOnMobile'

import styles from '../../../styles/ProductCardMobile.module.less'
import cardStyles from '../../../styles/ProductCard.module.less'

const SizeIndicator = dynamic(() => import('./SizeIndicator'))
const ColorIndicator = dynamic(() => import('./ColorIndicator'))
const AddToCartModal = dynamic(() => import('../../Modals/AddToCartModal'))

interface ProductCardMobileProps {
  product: Product
  isCarouselCard?: boolean
  onClick?: (event: MouseEvent<HTMLDivElement>) => void
}

const ProductCardMobile: FC<ProductCardMobileProps> = (props) => {
  const { product, onClick, isCarouselCard = false } = props

  const [isModalOpen, setIsModalOpen] = useState(false)

  const { locale: countryAndLocale } = useRouter()
  const { dispatchTrackingContext } = useTrackingContext()
  const productPriceFeatureFlag = useFeatureIsOn('product-card-mobile-price')
  const productRatingsFeatureFlag = useFeatureIsOn('product-card-mobile-ratings')
  const { t } = useTranslation('products')
  const analyticsLocation = useGetAnalyticsLocation('product_card')
  const { urlT } = useUrls()
  const openRFQModal = useRFQModal({
    analyticsLocation,
    product,
  })
  const isOnMobile = useIsOnMobile()

  const { country } = getCountryAndLocaleStrings(countryAndLocale)
  const { user, isVatIncluded } = useAuth()
  const { ratingQuantities } = useReviewFetch(product.id)
  const reviewsAverage = getAverageOfReviews(ratingQuantities)

  const {
    default: {
      price,
      reference: fullReferencePrice,
    },
    final: {
      reference,
    },
    discount: {
      lowest: lowestCustomTierDiscount,
    },
    hasCustomPrices,
    currency,
  } = usePrice(product, 1)

  const [wholePricePart, fractionalPricePart] = formatPriceNumber(reference).split(',')
  const freeShipping = isFreeDelivery(product, country, isVatIncluded)
  const productNameWithSalesCount = `${getProductName(product)}${getProductSalesCount(product, t)}`
  const { deliveryTime, minimumOrderValue } = getSupplierDeliveryDetails(
    product.supplier[0],
    country,
  )

  const deliveryText = deliveryHelper(deliveryTime, t, freeShipping)
  const productUrl = getPathName(product, t)
  const salesUnit = getSalesUnit(product)
  const isRFQProduct = !price

  const saveProductFeatureFlag = useFeatureIsOn('product-card-heart-icon')
  const colorIndicatorFeatureFlag = useFeatureIsOn('product-catalog-color-indicator')
  const sizeIndicatorFeatureFlag = useFeatureIsOn('product-catalog-size-indicator')

  const strikeThroughPrice = calculateStrikeThroughPrice(
    fullReferencePrice,
    lowestCustomTierDiscount,
    reference,
  )

  const renderIndicators = () => {
    const isColorVariant = getVariantType(product.product) === 'color'

    if (isCarouselCard) {
      return null
    }

    if (colorIndicatorFeatureFlag && isColorVariant) {
      return <ColorIndicator product={product} />
    }

    if (sizeIndicatorFeatureFlag) {
      return <SizeIndicator product={product} />
    }

    return null
  }

  const handleCartIconClick = () => {
    // Set add to cart analytics context to global state
    dispatchTrackingContext({
      eventName: 'add_to_cart',
      eventParameters: {
        context: analyticsLocation,
      },
    })

    setIsModalOpen(true)
  }

  return (
    // Key events unnecessary on mobile hence disabled for this div
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    <div
      className={styles['product-card']}
      onClick={onClick}
      tabIndex={0}
      role="button"
    >
      <Panel
        bodyFill
        className={styles['panel-border-style']}
      >
        <Link href={urlT(productUrl)}>
          <FlexboxGrid
            align="middle"
            justify="space-between"
            className={styles['top-container']}
          >
            {!isCarouselCard && (
              <p className={styles['delivery-text']}>
                {freeShipping && !isRFQProduct && t('Free')}
                  &nbsp;
                {isRFQProduct ? t('Quote only') : deliveryText}
              </p>
            )}
            {saveProductFeatureFlag ? (
              <SaveProductButton
                analyticsLocation={analyticsLocation}
                product={product}
                className={styles['save-button']}
                appearance="ghost"
                iconClassName={styles['heart-icon']}
              />
            ) : null}
          </FlexboxGrid>
          <div className={styles['img-container']}>
            {product.media?.length ? (
              <Image
                loader={imageLoader}
                src={getLargePreview(product.media?.[0])}
                alt={getTextContentByType(product, 'short')}
                fill
              />
            ) : (
              <ProductEmptyImage />
            )}
            <div className={`${cardStyles['indicator-container']} ${cardStyles['product-card-mobile']}`}>
              {renderIndicators()}
            </div>
          </div>
          {isCarouselCard && !isOnMobile && (
            <p className={styles['delivery-text-bottom']}>
              {freeShipping && !isRFQProduct && t('Free')}
                    &nbsp;
              {isRFQProduct ? t('Quote only') : deliveryText}
            </p>
          )}
          <FlexboxGrid>
            <div className={`${styles['max-width-100']} margin-top-spacer-half`}>
              <h6 className={styles['product-name']}>
                {productNameWithSalesCount}
              </h6>
              {isRFQProduct ? (
                <b className={styles['margin-right-spacer-half']}>
                  {t('Price available on request')}
                </b>
              )
                : (
                  <div className={styles['price-container']}>
                    {productPriceFeatureFlag ? (
                      <>
                        <b className={styles['whole-price']}>
                          {wholePricePart}
                        </b>
                        <div className={styles['right-side-container']}>
                          <b className={styles['fractional-price']}>{`${fractionalPricePart} ${currency}`}</b>
                          <p className={styles['per-piece']}>
                            /
                            {t(getPricePerPieceUnit(product), { count: 1 })}
                          </p>
                        </div>
                      </>
                    )
                      : (
                        <p>
                          {`${formatPriceNumber(reference)} ${currency}  /
                            ${t(getPricePerPieceUnit(product), { count: 1 })}`}
                        </p>
                      )}
                  </div>
                )}
              {!isRFQProduct && (
              <div>
                <b className={styles['strike-through']}>
                  {formatPriceNumber(strikeThroughPrice)}
                  {' '}
                  {currency}
                  {' '}
                  {'/ '}
                  {t(getPricePerPieceUnit(product), { count: 1 })}
                </b>
              </div>
              )}

              {productRatingsFeatureFlag && (
                <div className={styles['rating-container']}>
                  {!!reviewsAverage && (
                  <ReviewStarsWithDisplayedAverage
                    average={reviewsAverage}
                    hideNumbers
                  />
                  )}
                </div>
              )}

              {!isCarouselCard && (
                <p className="ellipsis">
                  <b>{product.supplier[0]['supplier.label']}</b>
                </p>
              )}

              {!isCarouselCard && (
                <p className={`ellipsis margin-zero ${styles['mov-text']}`}>
                  {minimumOrderValue
                    ? t('Min order {{value}} {{currency}}', {
                      value: formatPriceNumber(minimumOrderValue),
                      currency,
                    })
                    : t('No order minimum')}
                </p>
              )}

              {hasCustomPrices && (
              <Button
                appearance="ghost"
                onClick={(e) => e.preventDefault()}
                className={styles['contract-price-button']}
              >
                <NameTagContract
                  trigger="click"
                  name={user?.company ?? ''}
                  tooltipText={(
                    <SafeTranslate
                      ns="products"
                      i18nKey="{{buyer}} contract price for bulk purchasing starting from {{count}} {{salesUnit}} contract price available only for a limited time as per agreement"
                      values={{
                        buyer: user?.company,
                        count: lowestCustomTierDiscount?.['price.quantity'],
                        salesUnit: t(salesUnit, { count: lowestCustomTierDiscount?.['price.quantity'] }),
                      }}
                      components={{ br: <br /> }}
                    />
                  )}
                  customStyles={styles['contract-price-mobile']}
                />
              </Button>
              )}
            </div>
          </FlexboxGrid>
        </Link>

        {!isRFQProduct ? (
          <Button
            appearance="primary"
            block
            onClick={handleCartIconClick}
            className={styles['add-to-cart-button']}
          >
            <CartIcon size="sm" />
            <div className={styles['add-to-cart-text']}>
              {t('Add')}
            </div>
          </Button>
        )
          : (
            <Button
              appearance="primary"
              block
              onClick={() => openRFQModal()}
              className={styles['add-to-cart-button']}
            >
              <div className={styles['add-to-cart-text']}>
                {t('modals:Request quote')}
              </div>
            </Button>
          )}
      </Panel>
      {isModalOpen && (
        <AddToCartModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          product={product}
        />
      )}
    </div>
  )
}

export default memo(ProductCardMobile)
